/**
 * Editable + AI-personalizable section block for the CNPJ AEP report.
 *
 * Renders an inline editor + an "Gerar com IA" button below an existing AEP
 * section. The persisted text is shown right inside the printable area, so
 * the personalized commentary ends up in the final PDF.
 *
 * Usage:
 *   <EditableAISection
 *     cnpj={cnpj}
 *     sectionKey="plano"          // plano | recomendacoes_ia | plano_anual | absenteismo | conclusao
 *     label="Plano de ação consolidado"
 *   />
 *
 * Behavior:
 *   • Loads existing override via GET /cnpj/{cnpj}/aep-overrides on mount.
 *   • "Salvar"  -> PUT  /cnpj/{cnpj}/aep-overrides/{sk}
 *   • "Gerar IA" -> POST /cnpj/{cnpj}/aep-overrides/{sk}/ai (Claude Sonnet 4.6)
 *   • "Limpar"  -> DELETE /cnpj/{cnpj}/aep-overrides/{sk}
 *   • Toolbar is hidden in print (.no-print) — only the saved text prints.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function EditableAISection({ cnpj, sectionKey, label, overrides, onChange }) {
  const initial = overrides?.[sectionKey] || null;
  const [text, setText] = useState(initial?.custom_text || "");
  const [meta, setMeta] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (overrides?.[sectionKey]) {
      setMeta(overrides[sectionKey]);
      setText(overrides[sectionKey].custom_text || "");
      setDirty(false);
    }
  }, [overrides, sectionKey]);

  if (!cnpj) return null;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/cnpj/${cnpj}/aep-overrides/${sectionKey}`, { custom_text: text });
      setMeta(r.data);
      setDirty(false);
      onChange?.(sectionKey, r.data);
      toast.success("Comentário técnico salvo.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const generateWithAI = async () => {
    if (!window.confirm(
      `Gerar comentário técnico para "${label}" usando Claude Sonnet 4.6?\n\n` +
      `A IA usará os dados reais deste CNPJ (plano, recomendações, absenteísmo, etc.) ` +
      `para personalizar o texto. Pode levar 30-90 segundos.\n\n` +
      `O texto atual será substituído.`
    )) return;
    setGenerating(true);
    try {
      const r = await api.post(`/cnpj/${cnpj}/aep-overrides/${sectionKey}/ai`);
      setMeta(r.data);
      setText(r.data.custom_text || "");
      setDirty(false);
      onChange?.(sectionKey, r.data);
      toast.success("Comentário personalizado gerado pela IA.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gerar texto.");
    } finally {
      setGenerating(false);
    }
  };

  const clearOverride = async () => {
    if (!window.confirm("Remover o comentário técnico personalizado desta seção?")) return;
    try {
      await api.delete(`/cnpj/${cnpj}/aep-overrides/${sectionKey}`);
      setText("");
      setMeta(null);
      setDirty(false);
      onChange?.(sectionKey, null);
      toast.success("Comentário removido.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao remover.");
    }
  };

  const lastSourceLabel = meta?.last_source === "ai" ? "Gerado por IA" : "Editado manualmente";
  const lastWhen = meta?.updated_at
    ? new Date(meta.updated_at).toLocaleString("pt-BR")
    : null;

  return (
    <div className="mt-4 border border-emerald-200 bg-emerald-50/30 rounded-md print:bg-transparent print:border-emerald-200" data-testid={`aep-override-${sectionKey}`}>
      {/* Toolbar — hidden in print */}
      <div className="no-print flex flex-wrap items-center gap-2 px-3 py-2 border-b border-emerald-200 text-xs">
        <span className="font-bold uppercase tracking-wider text-emerald-900">
          <i className="fa-solid fa-pen-to-square mr-1"></i>
          Comentário técnico personalizado
        </span>
        <div className="flex-1"></div>
        {meta && (
          <span className="text-[10px] text-slate-500">
            {lastSourceLabel}
            {lastWhen ? ` · ${lastWhen}` : ""}
          </span>
        )}
        <button
          type="button"
          onClick={generateWithAI}
          disabled={generating || saving}
          className="inline-flex items-center gap-1 rounded bg-gradient-to-r from-emerald-700 to-emerald-600 text-white font-semibold px-3 py-1.5 disabled:opacity-50"
          data-testid={`aep-override-ai-btn-${sectionKey}`}
        >
          {generating
            ? <><i className="fa-solid fa-spinner fa-spin"></i> Gerando…</>
            : <><i className="fa-solid fa-wand-magic-sparkles"></i> Gerar com IA</>}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-1 rounded bg-white border border-emerald-700 text-emerald-800 font-semibold px-3 py-1.5 disabled:opacity-40"
          data-testid={`aep-override-save-btn-${sectionKey}`}
        >
          {saving
            ? <><i className="fa-solid fa-spinner fa-spin"></i></>
            : <><i className="fa-solid fa-floppy-disk"></i> Salvar</>}
          {!saving && dirty && <span>*</span>}
        </button>
        {meta && (
          <button
            type="button"
            onClick={clearOverride}
            className="inline-flex items-center gap-1 rounded border border-slate-300 text-slate-600 font-semibold px-2 py-1.5 hover:bg-slate-50"
            data-testid={`aep-override-clear-btn-${sectionKey}`}
            title="Remover comentário"
          >
            <i className="fa-solid fa-trash-can"></i>
          </button>
        )}
      </div>

      {/* Editor — hidden in print so the textarea border doesn't bleed into PDF */}
      <textarea
        className="no-print w-full p-3 text-sm leading-relaxed bg-white border-0 outline-none focus:ring-2 focus:ring-emerald-400 rounded-b-md font-sans"
        value={text}
        onChange={(e) => { setText(e.target.value); setDirty(true); }}
        rows={8}
        placeholder={`Digite aqui o comentário técnico personalizado para esta seção, ou clique em "Gerar com IA" para que a Claude Sonnet 4.6 escreva uma análise baseada nos dados reais deste CNPJ.`}
        data-testid={`aep-override-textarea-${sectionKey}`}
      />

      {/* Print-only view of the saved text */}
      {text?.trim() && (
        <div
          className="hidden print:block whitespace-pre-line text-[11pt] leading-relaxed text-slate-800 px-1 py-2"
          data-testid={`aep-override-print-${sectionKey}`}
        >
          {text}
        </div>
      )}
    </div>
  );
}
